import { db } from 'src/models/db'
import Papa from 'papaparse'
import { computed, type Ref } from 'vue'

export const exportGameToCSV = async (gameId: string) => {
  // Grab the chronological history
  const transactions = await db.transactions.where('gameId').equals(gameId).sortBy('sequence')

  // Map it to exactly the columns you want in Excel/Retrosheet
  const flattenedData = transactions.map((txn) => {
    // 1. Extract the base properties that exist on ALL transactions
    const baseData = {
      Game_ID: txn.gameId,
      Inning: txn.inning,
      Top_Bottom: txn.halfInning,
      Batter_ID: txn.batterId,
      Event_Type: txn.eventType,
    }
    // 2. Safely extract specific properties based on the eventType
    if (txn.eventType === 'PITCH') {
      // TypeScript now knows txn is definitely a PitchTransaction
      return {
        ...baseData,
        PitchResult: txn.pitchOutcome,
        Pitch_Type: txn.pitchType,
        Result: txn.outcome,
        BattedBallTrajector: txn.battedBallTrajectory,
        Location_X: txn.pitchX,
        Location_Y: txn.pitchY,
      }
    }
    if (txn.eventType === 'PLAY') {
      // TypeScript now knows txn is definitely a PlayTransaction
      return {
        ...baseData,
        Result: txn.outcome,
        Pitch_Type: '',
        Location_X: '',
        Location_Y: '',
      }
    }
    // 3. Fallback for Runner Advances (or any other future types)
    return {
      ...baseData,
      Result: '', // Runner advances don't use 'outcome' or 'pitchOutcome'
      Pitch_Type: '',
      Location_X: '',
      Location_Y: '',
    }
  })

  // Convert to CSV string
  const csvString = Papa.unparse(flattenedData)

  // Create a downloadable file in the browser
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `Game_${gameId}_Export.csv`
  link.click()
}

/**
 * Determines if a fielder is reaching to their glove side or backhand side.
 * Assumes a standard relative grid where Home Plate is roughly at X: 0.5, Y: 1.0
 */

export const calculateFieldingSide = (
    fielderRelX: number,
    fielderRelY: number,
    hitRelX: number,
    hitRelY: number,
    throwingHand: 'R' | 'L' | 'S' = 'R'
  ): { glove_hand: 'glove_side' | 'backhand' | 'straight_on'; dist: number } => {
    // Approximate Home Plate coordinates in standard relative space
    const homeX = 0.5
    const homeY = 1.0

    // 1. Vector from Fielder facing Home Plate
    const vFielderToHomeX = homeX - fielderRelX
    const vFielderToHomeY = homeY - fielderRelY

    // 2. Vector from Fielder moving to the Batted Ball
    const vFielderToHitX = hitRelX - fielderRelX
    const vFielderToHitY = hitRelY - fielderRelY

    // 3. 2D Cross Product to determine Left vs Right
    // Cross Product = (X1 * Y2) - (Y1 * X2)
    const crossProduct = (vFielderToHomeX * vFielderToHitY) - (vFielderToHomeY * vFielderToHitX)
    const dist = Math.abs(crossProduct)

    let glove_hand: 'glove_side' | 'backhand' | 'straight_on'

    // If the ball is hit almost perfectly at them, return straight_on
    if (dist < 0.02) {
      glove_hand = 'straight_on'
    } else {
      // In this coordinate system (0,0 top left, Home at bottom center):
      // A positive cross product means the ball is to the fielder's RIGHT.
      // A negative cross product means the ball is to the fielder's LEFT.
      const isHitToFieldersLeft = crossProduct < 0

      // 4. Map the physical direction to the player's glove hand
      if (throwingHand === 'R') {
        // Right-handed thrower wears glove on LEFT hand
        glove_hand = isHitToFieldersLeft ? 'glove_side' : 'backhand'
      } else {
        // Left-handed thrower wears glove on RIGHT hand
        glove_hand = isHitToFieldersLeft ? 'backhand' : 'glove_side'
      }
    }

    return { glove_hand, dist }
  }
/**
 * Shared layout configurations for drawing the responsive Konva baseball field
 * @param stageSize - A reactive reference to the canvas { width, height }
 */
export function useFieldLayout(stageSize: Ref<{ width: number; height: number }>) {
  // 0.95 gives a 5% margin at the bottom so home plate doesn't get clipped
  // const homeAnchorY = computed(() => stageSize.value.height * 0.95)

  // THE FIX: The drawn field is a wide wedge, not a perfect square.
  // By allowing the scale to reach 1.25x the height, the outfield arc can reach
  // the top of the container without being prematurely restricted by a wide screen width.
  const fieldScale = computed(() =>
    Math.min(stageSize.value.width * 0.96, stageSize.value.height * 1.05),
  )

  // Home plate acts as our anchor, sitting near the bottom center of the canvas.
  const homeAnchorX = computed(() => stageSize.value.width / 2)
  // 3. Center vertically!
  // Instead of gluing home plate to the absolute bottom of the screen,
  // we calculate the actual height of the drawn field (fieldScale * 0.75).
  // We take the exact vertical center of the canvas and push home plate
  // DOWN by half of that field height. This perfectly centers the entire
  // diamond in the middle of the screen, distributing empty space evenly!
  const homeAnchorY = computed(() => {
    const visualCenterY = stageSize.value.height / 2
    const halfFieldHeight = (fieldScale.value * 0.6) / 2
    const idealY = visualCenterY + halfFieldHeight

    // Protect the catcher position (which draws slightly below home plate)
    // by guaranteeing it never goes below 92% of the canvas height.
    return Math.min(idealY, stageSize.value.height * 0.92)
  })

  const backgroundConfig = computed(() => ({
    x: 0,
    y: 0,
    width: stageSize.value.width,
    height: stageSize.value.height,
    fill: '#ffffff',
    cornerRadius: 12,
  }))

  // entire outfield wedge boundary OUTER
  const outfieldWedgeConfig = computed(() => ({
    x: homeAnchorX.value,
    y: homeAnchorY.value,
    radius: fieldScale.value * 0.75, // Deep center field
    angle: 90,
    rotation: 225,
    fill: '#ffffff',
    stroke: '#2c3e50', // 2c3e50'
    strokeWidth: 2,
  }))

  // middle outfield
  const retroShortArcConfig = computed(() => ({
    x: homeAnchorX.value,
    y: homeAnchorY.value,
    innerRadius: fieldScale.value * 0.6,
    outerRadius: fieldScale.value * 0.6,
    angle: 90,
    rotation: 225,
    stroke: '#bdc3c7', //#bdc3c7
    strokeWidth: 1.5,
    dash: [4, 4],
  }))

  // infield boundary
  const infieldDirtConfig = computed(() => ({
    x: homeAnchorX.value,
    y: homeAnchorY.value,
    radius: fieldScale.value * 0.45,
    angle: 90,
    rotation: 225,
    fill: '#ffffff', // e2e4e9
    stroke: '#2c3e50', // 2c3e50
    strokeWidth: 2,
  }))

  // inner diamond w baselines
  const diamondConfig = computed(() => ({
    x: homeAnchorX.value,
    y: homeAnchorY.value - fieldScale.value * 0.17,
    sides: 4,
    radius: fieldScale.value * 0.17,
    fill: '#ffffff', //'#e2e4e9',
    stroke: '#2c3e50',
    strokeWidth: 2,
  }))

  const homePlateConfig = computed(() => ({
    x: homeAnchorX.value,
    y: homeAnchorY.value,
    sides: 5,
    radius: fieldScale.value * 0.03,
    fill: '#ffffff',
    stroke: '#2c3e50',
    strokeWidth: 1.5,
    rotation: 180,
  }))

  const firstBaseConfig = computed(() => ({
    x: homeAnchorX.value + fieldScale.value * 0.17,
    y: homeAnchorY.value - fieldScale.value * 0.17,
    sides: 4,
    radius: fieldScale.value * 0.03,
    fill: '#ffffff',
    stroke: '#2c3e50',
    strokeWidth: 1.5,
    rotation: 45,
  }))

  const secondBaseConfig = computed(() => ({
    x: homeAnchorX.value,
    y: homeAnchorY.value - fieldScale.value * 0.34,
    sides: 4,
    radius: fieldScale.value * 0.03,
    fill: '#ffffff',
    stroke: '#2c3e50',
    strokeWidth: 1.5,
    rotation: 45,
  }))

  const thirdBaseConfig = computed(() => ({
    x: homeAnchorX.value - fieldScale.value * 0.17,
    y: homeAnchorY.value - fieldScale.value * 0.17,
    sides: 4,
    radius: fieldScale.value * 0.03,
    fill: '#ffffff',
    stroke: '#2c3e50',
    strokeWidth: 1.5,
    rotation: 45,
  }))

  // --- NEW: Standard Fielding Coordinates ---
  // Calculates the physical X/Y location on our scaled canvas for any of the 9 positions
  const getStandardPositionCoords = (position: number) => {
    const hX = homeAnchorX.value
    const hY = homeAnchorY.value
    const scale = fieldScale.value

    switch (position) {
      case 1:
        return { x: hX, y: hY - scale * 0.17 } // P (Mound)
      case 2:
        return { x: hX, y: hY + scale * 0.06 } // C (Behind Home - shifted down)
      case 3:
        return { x: hX + scale * 0.19, y: hY - scale * 0.24 } // 1B (Behind line - shifted up)
      case 4:
        return {
          x: hX + Math.cos(285 * (Math.PI / 180)) * (scale * 0.38),
          y: hY + Math.sin(285 * (Math.PI / 180)) * (scale * 0.38),
        } // 2B (Centered at 285deg, right-center gap)
      case 5:
        return { x: hX - scale * 0.19, y: hY - scale * 0.24 } // 3B (Behind line - shifted up)
      case 6:
        return {
          x: hX + Math.cos(255 * (Math.PI / 180)) * (scale * 0.38),
          y: hY + Math.sin(255 * (Math.PI / 180)) * (scale * 0.38),
        } // SS (Centered at 255deg, left-center gap)
      case 7:
        return {
          x: hX + Math.cos(240 * (Math.PI / 180)) * (scale * 0.6),
          y: hY + Math.sin(240 * (Math.PI / 180)) * (scale * 0.6),
        } // LF (Centered at 240deg, exactly on the 0.6 short arc)
      case 8:
        return { x: hX, y: hY - scale * 0.6 } // CF (Directly center at 270deg)
      case 9:
        return {
          x: hX + Math.cos(300 * (Math.PI / 180)) * (scale * 0.6),
          y: hY + Math.sin(300 * (Math.PI / 180)) * (scale * 0.6),
        } // RF (Centered at 300deg, exactly on the 0.6 short arc)
      default:
        return { x: hX, y: hY }
    }
  }

  // --- RETROSHEET ZONE MODIFIERS ---
  // Left Center
  const retroLineLCConfig = computed(() => ({
    points: [
      homeAnchorX.value,
      homeAnchorY.value,
      homeAnchorX.value + Math.cos(255 * (Math.PI / 180)) * (fieldScale.value * 0.75),
      homeAnchorY.value + Math.sin(255 * (Math.PI / 180)) * (fieldScale.value * 0.75),
    ],
    stroke: '#bdc3c7', // bdc3c7
    strokeWidth: 1.5,
    dash: [4, 4],
  }))

  // Right Center
  const retroLineRCConfig = computed(() => ({
    points: [
      homeAnchorX.value,
      homeAnchorY.value,
      homeAnchorX.value + Math.cos(285 * (Math.PI / 180)) * (fieldScale.value * 0.75),
      homeAnchorY.value + Math.sin(285 * (Math.PI / 180)) * (fieldScale.value * 0.75),
    ],
    stroke: '#bdc3c7',
    strokeWidth: 1.5,
    dash: [4, 4],
  }))

  const getTrajectoryDash = (trajectory: string) => {
    switch (trajectory) {
      case 'L':
        return [] // Solid line for Line Drive
      case 'F':
        return [2, 5] // Dotted line for Fly ball
      case 'P':
        return [1, 6] // Widely dotted for Pop-up
      case 'G':
      default:
        return [6, 4] // Standard dash for Grounder
    }
  }

  function getPositionCircle(position: number) {
    const point = getStandardPositionCoords(position)

    const fieldRadius = fieldScale.value * 0.75
    const gap = fieldScale.value * 0.02

    const positions = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((p) => getStandardPositionCoords(p))

    const dFromHome = Math.hypot(point.x - homeAnchorX.value, point.y - homeAnchorY.value)

    const maxFenceRadius = fieldRadius - dFromHome

    let nearest = Infinity

    positions.forEach((p) => {
      if (p.x === point.x && p.y === point.y) return

      nearest = Math.min(nearest, Math.hypot(p.x - point.x, p.y - point.y))
    })
    const maxNeighborRadius = nearest / 2 - gap

    return {
      x: point.x,
      y: point.y,
      radius: Math.min(maxFenceRadius, maxNeighborRadius),
    }
  }

  return {
    homeAnchorX,
    homeAnchorY,
    fieldScale,
    backgroundConfig,
    outfieldWedgeConfig,
    infieldDirtConfig,
    diamondConfig,
    homePlateConfig,
    firstBaseConfig,
    secondBaseConfig,
    thirdBaseConfig,
    getStandardPositionCoords,
    retroLineLCConfig,
    retroLineRCConfig,
    retroShortArcConfig,
    getTrajectoryDash,
    getPositionCircle,
  }
}
